import React from "react";

export const project2en = {
  name: "project2",
  title: `Ecosystems of Siberia - Diversity and palaeoecological interpretation`,
  body: (
    <div>
      <div>
        <h3>Coordination</h3> Milan Chytrý (chytry@sci.muni.cz)
      </div>
      <br />
      <div>
        <h3>Participants at Masaryk University</h3> Irena Axmanová, Jiří Danihelka, Pavel Dřevojan,
        Michal Hájek, Ondřej Hájek, Petra Hájková, Michal Horsák, Martin Kočí, Klára Komprdová,
        Svatava Kubešová, Zdeňka Lososová, Pavel Lustyk, Salza Palpurina, Barbora Pelánková, Libor
        Petr, Zdenka Preislerová (Otýpková), Jan Roleček, Marcela Řezníčková, Veronika Schenková,
        Vít Syrovátka, Petr Šmarda, Lubomír Tichý, Viktoria Wagner, David Zelený
      </div>
      <br />
      <div>
        <div>
          <h3>Collaborators at other institutions</h3>
        </div>
        <li>Altai State University: Dmitry German, Aleksandr Shmakov</li>
        <li>
          Central Siberian Botanical Garden, Russian Academy of Sciences, Novosibirsk: Nikolai
          Ermakov, Andrei Korolyuk, Nikolai Lashchinskiy, Denis Popov
        </li>
        <li>Charles University, Prague: Petr Kuneš, Petr Pokorný</li>
        <li>
          Czech University of Life Sciences, Prague: Michal Hejcman, Pavla Hejcmanová, Miroslav
          Svoboda, Ondřej Sýkora, Volodymyr Trotsiuk
        </li>
        <li>
          Institute for Biological Problems of Cryolithozone, Russian Academy of Sciences, Yakutsk:
          Mikhail Cherosov
        </li>
        <li>
          Institute of Biology, Russian Academy of Sciences, Ufa: El&#39;vira Baisheva, Vasiliy
          Martynenko
        </li>
        <li>Institute of Biology, Russian Academy of Sciences, Ulan-Ude: Oleg Anenkhonov</li>
        <li>
          Institute of Botany, Academy of Sciences of the Czech Republic: Vlasta Jankovská, Helena
          Svobodová
        </li>
        <li>Institute of Botany, Slovak Academy of Sciences: Ivan Pišút, Milan Valachovič</li>
        <li>
          North-Eastern Federal University Named After M.K. Ammosov, Yakutsk: Paraskovia Gogoleva
        </li>
        <li>University of Graz: Philipp Resl</li>
        <li>University of New Mexico, Albuquerque: Jeffrey Nekola</li>
        <li>
          University of South Bohemia, České Budějovice: Martin Hais, Jan Novák, Věra Pavelková
          Řičánková, Jan Robovský
        </li>
      </div>
      <br />
      <div>
        <h3>Funding</h3>
        <li>2011–2015: Czech Science Foundation, project P504-11-0454</li>        
        <li>2008–2010: Ministry of Education of the Czech Republic, project MSM0021622416</li>
		<li>
          2003–2007: Grant Agency of the Academy of Sciences of the Czech Republic, project
          IAA6163303
        </li>
      </div>
      <br />
      <div>
        <h3>Project Aims</h3>
        <div>
          The project is focused on diversity of various components of natural ecosystems in
          different parts of Siberia and the Ural Mountains, with a special focus on plants,
          molluscs and mammals, i.e. important palaeoecological indicators. The project has two
          related general aims:
          <li>
            to describe patterns of large-scale species diversity in temperate and boreal ecosystems
            of continental northern Eurasia and reveal the underlying processes of diversity
            evolution
          </li>
          <li>
            to find potential analogues or relicts of Late Pleistocene and Early Holocene ecosystems
            that could improve our understanding of ecological processes that may have occurred in
            vanished ecosystems of northern Eurasia.
          </li>
        </div>
      </div>
      <br />
      <div className="main-results">
        <h3>Main results</h3>
        <p>
		   <b>Glacial refugium in the Altai-Sayan Mountains</b>
        <div>
        We discovered that the Altai-Sayan Mountains of Southern Siberia are probably the best
        preserved refugium of full-glacial biota in Eurasia. This finding was supported by
        considerable similarity of modern pollen deposition from the Western Sayan Mountains with
        fossil pollen spectra from the Late Pleistocene deposits in Central Europe (Kuneš et al.
        2008, Magyari et al. 2014), by the discovery of the whole community of terrestrial snails
        typical of the loess deposits from the coldest stages of the Pleistocene in the
        south-eastern Russian Altai (Horsák et al. 2010b), common occurrence of many glacial relict
        species of plant and snails in this region (Horsák et al. 2015) and by revealing high
        similarity between recent mammal assemblages of the south-eastern Altai-Sayan Mountains and
        the Pleistocene faunas of several regions in northern Eurasia (Pavelková Řičánková et al.
        2014, 2018). The rate of the mammal extinction in this region between the Last Glacial and the
        Recent is lowest in northern Eurasia (Pavelková Řičánková et al. 2015). A landscape mosaic of ecosystems of the south-eastern Russian Altai was described in detail as a modern analogue of the full-glacial steppe-tundra (Chytrý et al. 2019).
        </div>
		</p>
        <p>
          <b>Southern Ural forests are similar to Central Europan forests of the Early Holocene</b>
        <div>
        We interpreted forests of the Southern Urals as analogues of the Early Holocene forests of
        Central Europe. Ecology of these forests suggests that the spread of broad-leaved temperate
        trees may have reduced diversity of herb layer of the open Early Holocene forests, but at
        the same time it may have increased diversity of their snail communities (Chytrý et al.
        2010, Horsák et al. 2010a).
        </div>
		</p>
        <p>
          <b>The most species-rich forests in extratropical Eurasia</b>
        <div>
        We discovered that birch-pine hemiboreal forests in the northern Altai Mountains locally
        contain extremely high numbers of vascular plant species (up to 114 species per 100 m² or
        149 species per 1000 m²). These are so far the highest values of fine-scale species richness
        reported for forests in temperate and boreal Eurasia (Chytrý et al. 2012).
        </div>
		</p>
        <p>
          <b>Plant species richness patterns and their determinants</b>
        <div>
        Based on extensive standardized vegetation sampling, we characterized patterns of species richness in different regions of Siberia and adjacent regions, focusing on the effects of soil pH (Chytrý et al. 2007, Palpurina et al. 2017), climate continentality (Chytrý et al. 2008, Palpurina et al. 2017) and primary productivity (Axmanová et al. 2012, 2013, Palpurina et al. 2019).
        </div>
		</p>
        <p>
          <b>Modern pollen deposition in continental landscapes</b>
        <div>
        We identified pollen deposition in moss polsters and litter along landscape transects in the
        Altai and Western Sayan Mountains and related them to vegetation types (Pelánková et al.
        2008, Pelánková &amp; Chytrý 2009). This enabled us to improve the interpretion of full- and
        last-glacial vegetation of Central Europe based on fossil pollen (Kuneš et al. 2008, Magyari et al. 2014).
        </div>
		</p>
        <p>
          <b>Palaeoecology of Siberia</b>
		<div>
        For a better understanding of the glacial history of the refugial area in the Altai Mountains, we modelled habitat distribution in this region during the Last Glacial Maximum (Hais et al. 2015). This study suggested that this area was dominated by desert steppe. However, northern Siberia and Beringia were different: our study from this area, based on the analysis of plant remains from frozen gastrointestinal tracts of full-glacial megaherbivores, reconstructed a wet landscape with significant participation of tundra, mires, and also scattered woodlands and scrub (Axmanová et al. 2020). We also dealt with temperate relicts in the current Siberian landscape: using palaeoanthracological and dendroecological methods, we reconstructed the ecological history of <i>Tilia sibirica</i>, a relict temperate species surviving in southern Siberia (Novák et al. 2014).
        </div>
		</p>
        <p>
          <b>Ecology of mollusc communities</b>
        <div>
        Pioneering studies of snail faunas, their diversity, ecology and habitat requirements were
        conducted in the Southern Urals (Bashkortostan) (Horsák et al. 2010a), the Altai (Horsák et
        al. 2010b, 2015, 2018, Chytrý et al. 2019), Central Yakutia (Horsák et al. 2013) and the West Siberian Plain (Horsák
        &amp; Chytrý 2014).
        </div>
		</p>
        <p>
          <b>Vegetation survey</b>
        <div>
        We made detailed phytosociological descriptions of various types of natural vegetation in
        the northwestern Baikal region (Buryatia) (Chytrý et al. 1993, 1995, Danihelka &amp; Chytrý
        1995, Anenkhonov &amp; Chytrý 1998) and scree vegetation in the Altai-Sayan region (Ermakov
        et al. 2006). We formally established several syntaxa new to science.
        </div>
		</p>
        <p>
          <b>Taxonomic studies and important flora and fauna records</b>
        <div>
        We described several species of Siberian terrestrial snail as new for science, including <i>Vertigo botanicorum</i> (Horsák & Pokryszko 2010), <i>V. beringiana</i>, <i>V. chytryi</i>, <i>V. genesioides</i> (Nekola et al. 2018) and <i>Punctum lozeki</i> (Horsák & Meng 2018). Our genetic analyses confired that <i>Tilia sibirica</i> is a separate taxon that differs from European <i>Tilia cordata</i>. We also described a new intersectional hybrid of cinquefoil, <i>Potentilla</i> x <i>nebulosa</i>, from the Russian Altai (Danihelka & Soják 2012). In addition, we found another cinquefoil, <i>Potentilla turkestanica</i>, as a new species to the flora of Russia (Soják et al. 2011) and three species of mosses (<i>Bryum klinggraeffii</i>, <i>B. rubens</i> and <i>Dicranella staphylina</i>) as new species for the Asian part of Russia.
      </div>
		</p>
     </div> 
      <div className="project-publication">
        <div>
          <h3>Research papers</h3>
        </div>
		<li>
          <b>Axmanová I.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, Lustyk P., Kočí M., <b>Kubešová S.</b>, <b>Horsák M.</b>, Cherosov
          M.M. &amp; Gogoleva P.A. (2013): Plant species richness-productivity relationships in a
          low-productive boreal region. <i>Plant Ecology</i> 214: 207–219. – 
          <a href="http://link.springer.com/article/10.1007%2Fs11258-012-0160-z">
            {" "}
            fulltext on SpringerLink
          </a>
        </li>
        <li>
          <b>Axmanová I.</b>, <b>Chytrý M.</b>, <b>Zelený D.</b>, <b>Li C.-F.,</b> <b>Vymazalová M.</b>, <b>Danihelka J.</b>, <b>Horsák M.</b>, Kočí
          M., <b>Kubešová S.</b>, <b>Lososová Z.</b>, <b>Otýpková Z.</b>, <b>Tichý L.</b>, Martynenko V.B., Baisheva E.Z.,
          Schuster B. &amp; Diekmann M. (2012): The species richness-productivity relationship in
          the herb layer of European deciduous forests. <i>Global Ecology and Biogeography</i> 21: 657–667. – 
		  <a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1466-8238.2011.00707.x/abstract">{" "}fulltext in Wiley Online Library
          </a>
        </li>
		<li>
          <b>Axmanová I.</b>, Robovský J., <b>Tichý L. J.</b>, <b>Danihelka J.</b>, Troeva E., Protopopov A. &amp; <b>Chytrý M.</b> (2020): Habitats of Pleistocene megaherbivores reconstructed from the frozen fauna remains. <i>Ecography</i> 43: 703–713. – 
          <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1111/ecog.04940">
            {" "}
            fulltext in Wiley Online Library
          </a>
        </li>
        <li>
          <b>Chytrý M.</b> (2012): Database of Masaryk University&#39;s Vegetation Research in
          Siberia. <i>Biodiversity &amp; Ecology</i> 4: 290. – 
          <a href="http://www.biodiversity-plants.de/biodivers_ecol/publishing/b-e.00088.pdf">{" "}fulltext pdf file</a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Danihelka J.</b>, Ermakov N., <b>Hájek M.</b>, <b>Hájková P.</b>, Kočí M., <b>Kubešová S.</b>, Lustyk
          P., <b>Otýpková Z.</b>, Popov D., <b>Roleček J.</b>, <b>Řezníčková M.</b>, <b>Šmarda P.</b> &amp; Valachovič M.
          (2007): Plant species richness in continental southern Siberia: effects of pH and climate
          in the context of the species pool hypothesis. <i>Global Ecology and Biogeography</i> 16: 668–678. – 
		  <a href="https://www.academia.edu/14005392/Plant_species_richness_in_continental_southern_Siberia_effects_of_pH_and_climate_in_the_context_of_the_species_pool_hypothesis">{" "}fulltext</a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Horsák M.</b>, Kočí M., <b>Kubešová S.</b>, <b>Lososová Z.</b>, <b>Otýpková Z.</b>, Tichý
          L., Martynenko V.B. &amp; Baisheva E.Z. (2010): Modern analogues from the Southern Urals
          provide insights into biodiversity change in the early Holocene forests of Central
          Europe. <i>Journal of Biogeography</i> 37: 767–780. – 
		  <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1111/j.1365-2699.2009.02256.x">
            {" "}
            fulltext pdf file
          </a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Kubešová S.</b>, Lustyk P., Ermakov N., <b>Hájek M.</b>, <b>Hájková P.</b>, Kočí
          M., <b>Otýpková Z.</b>, <b>Roleček J.</b>, <b>Řezníčková M.</b>, <b>Šmarda P.</b>, Valachovič M., Popov D. &amp; Pišút
          I. (2008): Diversity of forest vegetation across a strong gradient of climatic
          continentality: Western Sayan Mountains, southern Siberia. <i>Plant Ecology</i> 196: 61–83. – 
          <a href="https://link.springer.com/article/10.1007/s11258-007-9335-4">
            {" "}
            fulltext on SpringerLink
          </a>
        </li>
        <li>
          <b>Chytrý M.</b>, Ermakov N., <b>Danihelka J.</b>, <b>Hájek M.</b>, <b>Hájková P.</b>, <b>Horsák M.</b>, Kočí M., <b>Kubešová
          S.</b>, Lustyk P., <b>Otýpková Z.</b>, <b>Pelánková B.</b>, Valachovič M. &amp; <b>Zelený D.</b> (2012): High
          species richness in hemiboreal forests of the northern Russian Altai, southern
          Siberia. <i>Journal of Vegetation Science</i> 23: 605–616. – 
          <a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1654-1103.2011.01383.x/abstract">
            {" "}
            fulltext in Wiley Online Library
          </a>
        </li>
        <li>
		<b>Chytrý M.</b>, <b>Horsák M.</b>, <b>Danihelka J.</b>, Ermakov N., German D.A., <b>Hájek M.</b>, <b>Hájková P.</b>, <b>Kočí M.</b>, <b>Kubešová S.</b>, <b>Lustyk P</b>., <b>Nekola J.C.</b>, Pavelková Řičánková V., <b>Preislerová Z.</b>, Resl P. & Valachovič M. (2019): A modern analogue of the Pleistocene steppe-tundra ecosystem in southern Siberia. <i>Boreas</i> 48: 36–56. – <a href="https://doi.org/10.1111/bor.12338">sharable read-only link</a>
		</li>
        <li>
		<b>Chytrý M.</b>, <b>Horsák M.</b>, <b>Syrovátka V.</b>, <b>Danihelka J.</b>, Ermakov N., German D.A., <b>Hájek M.</b>, <b>Hájek O.</b>, <b>Hájková P.</b>, <b>Horsáková V.</b>, <b>Kočí M.</b>, <b>Kubešová S.</b>, <b>Lustyk P.</b>, <b>Nekola J.C.</b>, <b>Preislerová Z.</b>, Resl P. & Valachovič M. (2017) Refugial ecosystems in central Asia as indicators of biodiversity change during the Pleistocene–Holocene transition. <i>Ecological Indicators</i> 77: 357–367. – <a href="http://dx.doi.org/10.1016/j.ecolind.2016.12.033">http://dx.doi.org/10.1016/j.ecolind.2016.12.033</a>
		</li>
        <li>
          <b>Chytrý M.</b>, Pešout P. &amp; Anenchonov O.A. (1993): Syntaxonomy of vegetation of Svjatoj
          Nos Peninsula, Lake Baikal. 1. Non forest communities. <i>Folia Geobotanica et Phytotaxonomica</i> 28: 337–383. – 
          <a href="http://www.sci.muni.cz/botany/chytry/Chytry_etal1993_Folia-Geobot.pdf">
            {" "}
            fulltex in pdf
          </a>
        </li>
        <li>
          <b>Danihelka J.</b> &amp; Soják J. (2012): <i>{" "}Potentilla x nebulosa</i> (Rosaceae), a new intersectional
          nothospecies from the Altai Mountains. <i>Turczaninowia</i> 5: 5–8. –
          <a href="http://is.muni.cz/repo/1090827/turcz_15_4_5-8.pdf">{" "}fulltext in pdf</a>{" "}
        </li>
        <li>
          Ermakov N., <b>Chytrý M.</b> &amp; Valachovič M. (2006): Vegetation of the rock outcrops and
          screes in the forest-steppe and steppe belts of the Altai and Western Sayan Mts., southern
          Siberia. <i>Phytocoenologia</i> 36: 509–545. –
          <a href="http://www.ingentaconnect.com/content/schweiz/phyt/2006/00000036/00000004/art00002">
            {" "}
            fulltext on IngentaConnect
          </a>
        </li>
        <li>
		Hais M., <b>Chytrý M.</b> & <b>Horsák M.</b> (2016) Exposure-related forest-steppe: A diverse landscape type determined by topography and climate. <i>Journal of Arid Environments</i> 135: 75–84. – <i>Phytocoenologia</i> 36: 509–545. –
          <a href="https://www.sciencedirect.com/science/article/abs/pii/S0140196316301537">
            {" "}
            fulltext on ScienceDirect
          </a>
		</li>
        <li>
          Hais M., Komprdová K., Ermakov N. &amp; <b>Chytrý M.</b> (2015): Modelling the Last Glacial
          Maximum environments for a refugium of Pleistocene biota in the Russian Altai Mountains,
          Siberia. <i>Palaeogeography, Palaeoclimatology, Palaeoecology</i> 438: 135–145. –
          <a href="http://www.sciencedirect.com/science/article/pii/S0031018215004137">
            {" "}
            fulltext on ScienceDirect
          </a>
        </li>
        <li>
          <b>Horsák M.</b> &amp; <b>Chytrý M.</b> (2014): Unimodal latitudinal pattern of land-snail species
          richness across northern Eurasian lowlands. <i>PLoS ONE</i> 9: e104035. –
          <a href="http://www.sci.muni.cz/botany/chytry/Horsak-Chytry2014_PLOS-ONE_with-appendix.pdf">
            {" "}
            fulltext in pdf
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b> &amp; <b>Axmanová I.</b> (2013): Exceptionally poor land snail fauna of
          central Yakutia (NE Russia): climatic and habitat determinants of species richness. <i>Polar
          Biology</i> 36: 185–191. –
          <a href="http://link.springer.com/article/10.1007%2Fs00300-012-1249-5">
            {" "}
            fulltext on SpringerLink
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, Kočí M., <b>Kubešová S.</b>, <b>Lososová Z.</b>, <b>Otýpková Z.</b> &amp;
          <b> Tichý L.</b> (2010): Snail faunas in the Southern Ural forests and their relations to
          vegetation: an analogue of the Early Holocene assemblages of Central Europe? <i>Journal of
          Molluscan Studies</i> 76: 1–10. –
          <a href="https://academic.oup.com/mollus/article/76/1/1/1084149">
            {" "}
            fulltext on Oxford Journals
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b>, <b>Hájková P.</b>, <b>Hájek M.</b>, <b>Danihelka J.</b>, <b>Horsáková V.</b>, Ermakov N., German
          D.A., Kočí M., Lustyk P., Nekola J.C., <b>Preislerová Z.</b> &amp; Valachovič M. (2015): European
          glacial relict snails and plants: environmental context of their modern refugial
          occurrence in southern Siberia. <i>Boreas</i> 44: 638–657. –
          <a href="http://onlinelibrary.wiley.com/doi/10.1111/bor.12133/abstract">
            {" "}
            fulltext on Wiley Online Library
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b>, Pokryszko B.M., <b>Danihelka J.</b>, Ermakov N., <b>Hájek M.</b>, <b>Hájková P.</b>, 
          <b> Kintrová K.</b>, Kočí M., <b>Kubešová S.</b>, Lustyk P., <b>Otýpková Z.</b>, <b>Pelánková B.</b> &amp; Valachovič
          M. (2010): Habitats of relict terrestrial snails in southern Siberia: lessons for the
          reconstruction of palaeoenvironments of full-glacial Europe. <i>Journal of Biogeography</i> 37:
          1450–1462. –
          <a href="https://onlinelibrary.wiley.com/doi/full/10.1111/j.1365-2699.2010.02280.x">
            {" "}
            fulltext in Wiley Online Library
          </a>
        </li>
        <li>
		<b>Horsák M.</b> & Meng S. (2018): <i>Punctum lozeki</i> n. sp. – a new minute land-snail species (Gastropoda: Punctidae) from Siberia and Alaska. <i>Malacologia</i> 62: 11–20. 
		 </li>
        <li>
		<b>Horsák M.</b>, Juřičková L., <b>Horsáková V.</b>, Pokorná A., Pokorný P., Šizling A. & <b>Chytrý M.</b> (2018) Forest snail diversity and its environmental predictors along a sharp climatic gradient in southern Siberia. <i>Acta Oecologica</i> 88: 1–8. – <a href="https://doi.org/10.1016/j.actao.2018.02.009">
            {" "}
            https://doi.org/10.1016/j.actao.2018.02.009
          </a>
		</li>
        <li>
          <b>Horsák M.</b> &amp; Pokryszko B.M. (2010): <i>Vertigo botanicorum</i> sp. nov. (Gastropoda:
          Pulmonata: Vertiginidae)-a new whorl-snail from the Russian Altai Mountains. <i>Zootaxa</i> 2634:
          57–62. –
          <a href="http://www.mapress.com/zootaxa/2010/f/z02634p062f.pdf">fulltext in pdf</a>{" "}
        </li>
        <li>
          Kuneš P., <b>Pelánková B.</b>, <b>Chytrý M.</b>, Jankovská V., Pokorný P. &amp; Petr L. (2008):
          Interpretation of the last-glacial vegetation of eastern-central Europe using modern
          analogues from southern Siberia. <i>Journal of Biogeography</i> 35: 2223–2236. –
          <a href="https://onlinelibrary.wiley.com/doi/10.1111/j.1365-2699.2008.01974.x">
            {" "}
            fulltext on Wiley InterScience
          </a>
        </li>
        <li>
		Logan S.A., <b>Chytrý M.</b> & Wolff K. (2018): Genetic diversity and demographic history of the Siberian lime (Tilia sibirica). Perspectives in Plant Ecology, Evolution and Systematics 33: 9–17. – <a href="https://doi.org/10.1016/j.ppees.2018.04.005">
            {" "}https://doi.org/10.1016/j.ppees.2018.04.005</a>
		 </li>
        <li>
          Magyari E.K., Kuneš P., Jakab G., Sümegi P., <b>Pelánková B.</b>, Schäbitz F., Braun M. &amp;
          <b> Chytrý M.</b> (2014): Late Pleniglacial vegetation in eastern-central Europe: are there modern
          analogues in Siberia? <i>Quaternary Science Reviews</i> 95: 60–79. –
          <a href="http://www.sciencedirect.com/science/article/pii/S0277379114001449">
            {" "}
            fulltext in ScienceDirect
          </a>
        </li>
        <li>
		<b>Nekola J.C.</b>, Chiba S., Coles B.F., Drost C.A., von Proschwitz T. & <b>Horsák M.</b> (2018): A phylogenetic overview of the genus Vertigo O. F. Müller 1773 (Gastropoda: Pulmonata: Pupillidae: Vertigininae). <i>Malacologia</i> 62: 21–161.
		</li>
        <li>
          Nekola J.C., Coles B.F. &amp; <b>Horsák M.</b> (2015): Species assignment in Pupilla (Gastropoda:
          Pulmonata: Pupillidae): integration of DNA-sequence data and conchology. <i>Journal of
          Molluscan Studies</i> 81: 196–216. –
          <a href="http://mollus.oxfordjournals.org/content/early/2014/12/20/mollus.eyu083">
            {" "}
            fulltext on Oxford Journals
          </a>
        </li>
        <li>
          Novák J., Trotsiuk V., Sýkora O., Svoboda M. &amp; <b>Chytrý M.</b> (2014): Ecology of <i>Tilia
          sibirica</i> in a continental hemiboreal forest, southern Siberia: An analogue of a glacial
          refugium of broad-leaved temperate trees? <i>The Holocene</i> 24: 908–918. –
          <a href="http://hol.sagepub.com/content/24/8/908"> fulltext in SAGE Journals</a>
        </li>
        <li>
		<b>Palpurina S.</b>, <b>Chytrý M.</b>, Hölzel N., <b>Tichý L.</b>, Wagner V., <b>Horsák M.</b>, <b>Axmanová I.</b>, <b>Hájek M.</b>, <b>Hájková P.</b>, Freitag M., <b>Lososová Z.</b>, Mathar W., Tzonev R., <b>Danihelka J.</b> & <b>Dřevojan P.</b> (2019) The type of nutrient limitation affects the plant species richness–productivity relationship: Evidence from dry grasslands across Eurasia. <i>Journal of Ecology</i> 107: 1038–1050. – <a href="https://doi.org/10.1111/1365-2745.13084">https://doi.org/10.1111/1365-2745.13084</a>
		</li>
		<li>
		<b>Palpurina S.</b>, Wagner V., von Wehrden H., <b>Hájek M.</b>, <b>Horsák M.</b>, Brinkert A., Hölzel N., Wesche K., Kamp J., <b>Hájková P.</b>, <b>Danihelka J.</b>, Lustyk P., <b>Merunková K.</b>, <b>Preislerová Z.</b>, <b>Kočí M.</b>, <b>Kubešová S.</b>, Cherosov M., Ermakov N., German D., Gogoleva P., Lashchinsky N., Martynenko V. & <b>Chytrý M.</b> (2017) The relationship between plant species richness and soil pH vanishes with increasing aridity across Eurasian dry grasslands. <i>Global Ecology and Biogeography</i> 26: 425–434. DOI: 10.1111/geb.12549. – <a href="https://onlinelibrary.wiley.com/doi/abs/10.1111/geb.12549">fulltext in Wiley Online Library</a>
		</li>
		<li>
		Pavelková Řičánková V., <b>Horsák M.</b>, Hais M., Robovský J. & <b>Chytrý M.</b> (2018): Environmental correlates of the Late Quaternary regional extinctions of large and small Palaearctic mammals. <i>Ecography</i> 41: 516–527. – <a href="http://dx.doi.org/10.1111/ecog.02851">http://dx.doi.org/10.1111/ecog.02851</a>
		</li>
		<li>
          Pavelková Řičánková V., Riegert J., Semančíková E., Hais M., Čejková A. &amp; Prach K.
          (2014): Habitat preferences in gray marmots (<i>Marmota baibacina</i>). <i>Acta Theriologica</i> 59:
          317–324. – <a href="https://www.academia.edu/14618209/Habitat_preferences_in_gray_marmots_Marmota_baibacina_">fulltext in pdf</a>
        </li>
        <li>
          Pavelková Řičánková V., Robovský J. &amp; Riegert J. (2014): Ecological Structure of
          Recent and Last Glacial Mammalian Faunas in Northern Eurasia: The Case of Altai-Sayan
          Refugium. <i>PLoS ONE</i> 9: e85056. –
          <a href="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0085056">
            {" "}
            fulltext in pdf
          </a>
        </li>
        <li>
          Pavelková Řičánková V., Robovský J., Riegert J. &amp; Zrzavý J. (2015): Regional patterns
          of postglacial changes in the Palearctic mammalian diversity indicate retreat to Siberian
          steppes rather than extinction. <i>Scientific Reports</i> 5: 12682. –
          <a href="http://botzool.sci.muni.cz/pubs/Pavelkova-Ricankova_etal2015_Sci-Rep.pdf">
            {" "}
            fulltext in pdf
          </a>
        </li>
        <li>
          <b>Pelánková B.</b> &amp; <b>Chytrý M.</b> (2009): Surface pollen-vegetation relationships in the
          forest- steppe, taiga and tundra landscapes of the Russian Altai Mountains. <i>Review of
          Palaeobotany and Palynology</i> 157: 253–265. –
          <a href="https://www.sciencedirect.com/science/article/pii/S0034666709000906">
            {" "}
            fulltext on ScienceDirect
          </a>
        </li>
        <li>
          <b>Pelánková B.</b>, Kuneš P., <b>Chytrý M.</b>, Jankovská V., Ermakov N. &amp; Svobodová-Svitavská H.
          (2008): The relationships of modern pollen spectra to vegetation and climate along a
          steppe-forest-tundra transition in southern Siberia, explored by decision trees. <i>The
          Holocene</i> 18: 1259–1271. –
          <a href="http://hol.sagepub.com/cgi/content/abstract/18/8/1259">
            {" "}
            fulltext on Sage Journals Online
          </a>
        </li>
        <li>
          Soják J., <b>Danihelka J.</b> &amp; <b>Hájek M.</b> (2011): Potentilla turkestanica, a rare species new
          to the flora of Russia. <i>Turczaninowia</i> 14: 101–105. –
          <a href="http://www.sci.muni.cz/botany/vz/pdf/Sojak_Danihelka_Hajek_2011_Potentilla_turkestanica_turcz_14_2_101-105.pdf">
            {" "}
            fulltext in pdf
          </a>
        </li>
		<li>
		Wagner V., <b>Chytrý M.</b>, <b>Zelený D.</b>, von Wehrden H., Brinkert A., <b>Danihelka J.</b>, Hölzel N., Jansen F., Kamp J., Lustyk P., <b>Merunková K.</b>, <b>Palpurina S.</b>, <b>Preislerová Z.</b> & Wesche K. (2017) Regional differences in soil pH niche among dry grassland plants in Eurasia. <i>Oikos</i> 126: 660–670. – <a href="http://dx.doi.org/10.1111/oik.03369">
            {" "}
            http://dx.doi.org/10.1111/oik.03369
          </a>
		</li>
      
	  <div>
	   <h3>Media</h3>
        </div>
       <li>
          30/01/2014{" "}
          <a href="http://www.rferl.org/content/russia-mongolia-altai-ice-age-species/25247802.html">
            Ice age thrives in Southern Siberia
          </a>
          , Radio Free Europe
        </li>
        <li>
          23/01/2014{" "}
          <a href="http://www.newscientist.com/article/mg22129533.800-iceage-animals-live-on-in-eurasian-mountain-range.html#.UygwyNtIw8E">
            Ice-age animals live on in Eurasian mountain range
          </a>
          , New Scientist
        </li>
		</div>
    </div>
  )
};

export const project2cz = {
  name: "project2",
  title: `Ekosystémy Sibiře a jejich paleoekologická interpretace`,
  body: (
    <div>
      <div>
        <h3>Koordinátor</h3> Milan Chytrý (chytry@sci.muni.cz)
      </div>
      <br />
      <div>
        <h3>Spolupracovníci (Masarykova univerzita)</h3> Irena Axmanová, Jiří Danihelka, Pavel
        Dřevojan, Michal Hájek, Ondřej Hájek, Petra Hájková, Michal Horsák, Martin Kočí, Klára
        Komprdová, Svatava Kubešová, Zdeňka Lososová, Pavel Lustyk, Salza Palpurina, Barbora
        Pelánková, Libor Petr, Zdenka Preislerová (Otýpková), Jan Roleček, Marcela Řezníčková,
        Veronika Schenková, Vít Syrovátka, Petr Šmarda, Lubomír Tichý, Viktoria Wagner, David Zelený
      </div>
      <br />
      <div>
        <div>
          <h3>Spolupracovníci (ostatní instituce)</h3>
        </div>
        <li>Altai State University: Dmitry German, Aleksandr Shmakov</li>
        <li>
          Central Siberian Botanical Garden, Russian Academy of Sciences, Novosibirsk: Nikolai
          Ermakov, Andrei Korolyuk, Nikolai Lashchinskiy, Denis Popov
        </li>
        <li>Univerzita Karlova, Praha: Petr Kuneš, Petr Pokorný</li>
        <li>
          Česká zemědělsá univerzita v Praze: Michal Hejcman, Pavla Hejcmanová, Miroslav Svoboda,
          Ondřej Sýkora, Volodymyr Trotsiuk
        </li>
        <li>
          Institute for Biological Problems of Cryolithozone, Russian Academy of Sciences, Yakutsk:
          Mikhail Cherosov
        </li>
        <li>
          Institute of Biology, Russian Academy of Sciences, Ufa: El&#39;vira Baisheva, Vasiliy
          Martynenko
        </li>
        <li>Institute of Biology, Russian Academy of Sciences, Ulan-Ude: Oleg Anenkhonov</li>
        <li>Botanický ústav, Akademie věd České republiky: Vlasta Jankovská, Helena Svobodová</li>
        <li>Botanický ústav, Slovenská akademie věd: Ivan Pišút, Milan Valachovič</li>
        <li>
          North-Eastern Federal University Named After M.K. Ammosov, Yakutsk: Paraskovia Gogoleva
        </li>
        <li>University of Graz: Philipp Resl</li>
        <li>University of New Mexico, Albuquerque: Jeffrey Nekola</li>
        <li>
          Jihočeská univerzita, České Budějovice: Martin Hais, Jan Novák, Věra Pavelková Řičánková,
          Jan Robovský
        </li>
      </div>
      <br />
      <div>
        <h3>Finanční podpora</h3>
        <li>2011–2015: Grantová agentura České republiky, projekt P504-11-0454</li>
        <li>2008–2010: Ministerstvo školství, mládeže a tělovýchovy ČR, projekt MSM0021622416</li>
        <li>2003–2007: Grantová agentura Akademie věd ČR, projekt IAA6163303</li>
      </div>
      <br />
      <div>
        <h3>Cíle projektu</h3>
        <div>
          Projekt zkoumá diverzitu různých složek přirozených ekosystémů Sibiře a přilehlých oblastí. Speciálně se zaměřuje na rostliny, měkkýše a savce, tedy významné paleoekologické indikátory. Dva hlavní cíle projektu jsou:
          <li>
            popsat struktury rozmístění druhové diverzity v temperátních a boreálních ekosystémech kontinentální severní Eurasie a odhalit procesy, které stály za jejich vznikem;
          </li>
          <li>
            najít potenciální analogie nebo relikty pozdně pleistocenních a raně holocenních ekosystémů, které by mohly zlepšit naše znalosti o ekologických procesech probíhajících v těchto dávno zaniklých ekosystémech severní Eurasie.
          </li>
        </div>
      </div>
      <br />
      <div className="main-results">
        <h3>Hlavní výsledky</h3>
        <p>
		   <b>Glaciální refugium v horách Altaje a Západního Sajanu</b>
        <div>
        V horách Altaje a Západního Sajanu jsme objevili pravděpodobně nejlépe zachovalé refugium vrcholně glaciální bioty v Eurasii. Toto zjištění je podpořeno výraznou podobností moderního pylového spadu z těchto pohoří s fosilními pylovými spektry z pozdně pleistocenních sedimentů ve střední Evropě (Kuneš et al. 2008, Magyari et al. 2014), objevem celého společenstva terestrických měkkýšů typických pro sprašové sedimenty nejchladnějších fází pleistocénu na jihovýchodě ruského Altaje (Horsák et al. 2010b), běžným výskytem mnoha glaciálních reliktních rostlin a plžů v této oblasti (Horsák et al. 2015) a zjištěním velké podobnosti mezi současnou faunou savců jihovýchodního ruského Altaje a Sajanu a pleistocenními faunami několika regionů severní Eurasie (Pavelková Řičánková et al. 2014). Podíl savců, kteří v tomto území vymřeli mezi posledním glaciálem a současností je nejmenší v severní Eurasii (Pavelková Řičánková et al. 2015, 2018). Krajinná mozaika ekosystémů jihovýchodního ruského Altaje byla podrobně popsána jako současná analogie vrcholně glaciální stepotundry (Chytrý et al. 2019).
        </div>
		</p>
        <p>
          <b>Lesy jižního Uralu jsou podobné středoevropským lesům raného holocénu</b>
        <div>
        Lesy Jižního Uralu jsme interpretovali jako analogii raně holocenních lesů střední Evropy. Ekologie těchto lesů naznačuje, že šíření širolistých temperátních stromů mohlo omezit diverzitu bylinného patra světlých raně holocenních lesů, ale současně mohlo vést ke zvýšení diverzity společenstev lesních měkkýšů (Chytrý et al. 2010, Horsák et al. 2010a).
        </div>
		</p>
        <p>
          <b>Druhově nejbohatší lesy mimotropické Eurasie</b>
        <div>
        Na severním Altaji jsme objevili březovo-borové hemiboreální lesy s extrémně vysokou druhovou diverzitou v podrostu (až 114 druhů na 100 m² a 149 druhů na 1000 m²). Jde o dosud největší zjištěné hodnoty maloplošné druhové diverzity zaznamenané v lesích temperátní a boreální Eurasie (Chytrý et al. 2012).
        </div>
		</p>
        <p>
          <b>Rozložení druhového bohatství v krajině a jeho příčiny</b>
        <div>
        Na základě rozsáhlého standardizovaného sběru vegetačních dat v různých částech Sibiře a přilehlých územích jsme popsali rozložení druhového bohatství v krajině v závislosti na různých faktorech, zejména pH půdy (Chytrý et al. 2007, Palpurina et al. 2017), klimatické kontinentality (Chytrý et al. 2008, Palpurina et al. 2017) a primární produktivity (Axmanová et al. 2012, 2013, Palpurina et al. 2019).
        </div>
		</p>
        <p>
          <b>Současný pylový spad v kontinentálních krajinách</b>
        <div>
        Na transektech přes krajinu Altaje a Západního Sajanu jsme prozkoumali spad pylu zachyceného v mechových polštářích a humusu a vztáhli jej k okolním vegetačním typům (Pelánková et al. 2008, Pelánková & Chytrý 2009). To umožnilo zlepšit interpretaci vrcholně a pozdně glaciální vegetace střední Evropy na základě fosilních pylových spekter (Kuneš et al. 2008, Magyari et al. 2014).
        </div>
		</p>
        <p>
          <b>Paleoekologie Sibiře</b>
        <div>
        Pro lepší pochopení glaciální historie refugiálního území na Altaji jsme vytvořili modely rozšíření biotopů v této oblasti během posledního glaciálního maxima (Hais et al. 2015). Ty naznačují, že v tamní krajině tehdy převládaly pustinné stepi. Na severu Sibiře a v Beringii byla ale situace jiná: naše studie z této oblasti založená na analýze rostlinných zbytků ze zmrzlých žaludků a střev vrcholně glaciálních býložravců rekonstruovala vlhkou krajinu s výrazným zastoupením tundry, rašelinišť i lesíků a křovin (Axmanová et al. 2020). Zabývali jsme se i temperátními relikty v současné sibiřské krajině: pomocí paleoantrakologických a dendroekologických metod jsme rekonstruovali ekologickou historii lípy sibiřské (<i>Tilia sibirica</i>), reliktního druhu přežívajícího na jižní Sibiři (Novák et al. 2014).
        </div>
		</p>
        <p>
          <b>Ekologie společenstev měkkýšů</b>
        <div>
        Pionýrské studie faun měkkýšů, jejich diverzity, ekologie a stanovištních nároků byly provedeny na Jižním Uralu (Baškortostán) (Horsák et al. 2010a), Altaji (Horsák et al. 2010b, 2015, 2018, Chytrý et al. 2019), ve středním Jakutsku (Horsák et al. 2013) a na Západosibiřské nížině (Horsák & Chytrý 2014).
        </div>
		</p>
        <p>
          <b>Fytocenologický výzkum</b>
        <div>
        Provedli jsme pionýrské fytocenogické zhodnocení různých typů přirozené vegetace severního Zabajkalí v Burjatsku (Chytrý et al. 1993, 1995, Danihelka & Chytrý 1995, Anenkhonov & Chytrý 1998) a suťové vegetace Altaje a Západního Sajanu (Ermakov et al. 2006). Formálně jsme popsali několik syntaxonů nových pro vědu.
        </div>
		</p>
        <p>
          <b>Taxonomické studie a významné floristické a faunistické nálezy</b>
        <div>
        Popsali jsme několik druhů sibiřských terestrických plžů nových pro vědu: <i>Vertigo botanicorum</i> (Horsák & Pokryszko 2010), <i>V. beringiana, V. chytryi, V. genesioides</i> (Nekola et al. 2018) a <i>Punctum lozeki</i> (Horsák & Meng 2018). Genetickými analýzami jsme potvrdili, že lípa sibiřská (<i>Tilia sibirica</i>) je samostatným druhem odlišným od evropské lípy srdčité (<i>Tilia cordata</i>) (Logan et al. 2018). Z ruského Altaje jsme popsali nového mezirodového hybrida mochny, <i>Potentilla</i> x <i>nebulosa</i> (Danihelka & Soják 2012). Dále jsme nalezli mochnu <i>Potentilla turkestanica</i> na Altaji jako nový druh pro flóru Ruska (Soják et al. 2011) a tři druhy mechů (<i>Bryum klinggraeffii, B. rubens</i> a <i>Dicranella staphylina</i>) jako nové pro asijskou část Ruska.
      </div>
		</p>
     </div>
      <div className="project-publication">
        <div>
          <h3>Odborné publikace</h3>
        </div>
		<li>
          <b>Axmanová I.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, Lustyk P., Kočí M., <b>Kubešová S.</b>, <b>Horsák M.</b>, Cherosov
          M.M. &amp; Gogoleva P.A. (2013): Plant species richness-productivity relationships in a
          low-productive boreal region. <i>Plant Ecology</i> 214: 207–219. – 
          <a href="http://link.springer.com/article/10.1007%2Fs11258-012-0160-z">
            {" "}
            fulltext on SpringerLink
          </a>
        </li>
        <li>
          <b>Axmanová I.</b>, <b>Chytrý M.</b>, <b>Zelený D.</b>, <b>Li C.-F.,</b> <b>Vymazalová M.</b>, <b>Danihelka J.</b>, <b>Horsák M.</b>, Kočí
          M., <b>Kubešová S.</b>, <b>Lososová Z.</b>, <b>Otýpková Z.</b>, <b>Tichý L.</b>, Martynenko V.B., Baisheva E.Z.,
          Schuster B. &amp; Diekmann M. (2012): The species richness-productivity relationship in
          the herb layer of European deciduous forests. <i>Global Ecology and Biogeography</i> 21: 657–667. – 
		  <a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1466-8238.2011.00707.x/abstract">{" "}fulltext in Wiley Online Library
          </a>
        </li>
		<li>
          <b>Axmanová I.</b>, Robovský J., <b>Tichý L. J.</b>, <b>Danihelka J.</b>, Troeva E., Protopopov A. &amp; <b>Chytrý M.</b> (2020): Habitats of Pleistocene megaherbivores reconstructed from the frozen fauna remains. <i>Ecography</i> 43: 703–713. – 
          <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1111/ecog.04940">
            {" "}
            fulltext in Wiley Online Library
          </a>
        </li>
        <li>
          <b>Chytrý M.</b> (2012): Database of Masaryk University&#39;s Vegetation Research in
          Siberia. <i>Biodiversity &amp; Ecology</i> 4: 290. – 
          <a href="http://www.biodiversity-plants.de/biodivers_ecol/publishing/b-e.00088.pdf">{" "}fulltext pdf file</a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Danihelka J.</b>, Ermakov N., <b>Hájek M.</b>, <b>Hájková P.</b>, Kočí M., <b>Kubešová S.</b>, Lustyk
          P., <b>Otýpková Z.</b>, Popov D., <b>Roleček J.</b>, <b>Řezníčková M.</b>, <b>Šmarda P.</b> &amp; Valachovič M.
          (2007): Plant species richness in continental southern Siberia: effects of pH and climate
          in the context of the species pool hypothesis. <i>Global Ecology and Biogeography</i> 16: 668–678. – 
		  <a href="https://www.academia.edu/14005392/Plant_species_richness_in_continental_southern_Siberia_effects_of_pH_and_climate_in_the_context_of_the_species_pool_hypothesis">{" "}fulltext</a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Horsák M.</b>, Kočí M., <b>Kubešová S.</b>, <b>Lososová Z.</b>, <b>Otýpková Z.</b>, Tichý
          L., Martynenko V.B. &amp; Baisheva E.Z. (2010): Modern analogues from the Southern Urals
          provide insights into biodiversity change in the early Holocene forests of Central
          Europe. <i>Journal of Biogeography</i> 37: 767–780. – 
		  <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1111/j.1365-2699.2009.02256.x">
            {" "}
            fulltext pdf file
          </a>
        </li>
        <li>
          <b>Chytrý M.</b>, <b>Danihelka J.</b>, <b>Kubešová S.</b>, Lustyk P., Ermakov N., <b>Hájek M.</b>, <b>Hájková P.</b>, Kočí
          M., <b>Otýpková Z.</b>, <b>Roleček J.</b>, <b>Řezníčková M.</b>, <b>Šmarda P.</b>, Valachovič M., Popov D. &amp; Pišút
          I. (2008): Diversity of forest vegetation across a strong gradient of climatic
          continentality: Western Sayan Mountains, southern Siberia. <i>Plant Ecology</i> 196: 61–83. – 
          <a href="https://link.springer.com/article/10.1007/s11258-007-9335-4">
            {" "}
            fulltext on SpringerLink
          </a>
        </li>
        <li>
          <b>Chytrý M.</b>, Ermakov N., <b>Danihelka J.</b>, <b>Hájek M.</b>, <b>Hájková P.</b>, <b>Horsák M.</b>, Kočí M., <b>Kubešová
          S.</b>, Lustyk P., <b>Otýpková Z.</b>, <b>Pelánková B.</b>, Valachovič M. &amp; <b>Zelený D.</b> (2012): High
          species richness in hemiboreal forests of the northern Russian Altai, southern
          Siberia. <i>Journal of Vegetation Science</i> 23: 605–616. – 
          <a href="http://onlinelibrary.wiley.com/doi/10.1111/j.1654-1103.2011.01383.x/abstract">
            {" "}
            fulltext in Wiley Online Library
          </a>
        </li>
        <li>
		<b>Chytrý M.</b>, <b>Horsák M.</b>, <b>Danihelka J.</b>, Ermakov N., German D.A., <b>Hájek M.</b>, <b>Hájková P.</b>, <b>Kočí M.</b>, <b>Kubešová S.</b>, <b>Lustyk P</b>., <b>Nekola J.C.</b>, Pavelková Řičánková V., <b>Preislerová Z.</b>, Resl P. & Valachovič M. (2019): A modern analogue of the Pleistocene steppe-tundra ecosystem in southern Siberia. <i>Boreas</i> 48: 36–56. – <a href="https://doi.org/10.1111/bor.12338">sharable read-only link</a>
		</li>
        <li>
		<b>Chytrý M.</b>, <b>Horsák M.</b>, <b>Syrovátka V.</b>, <b>Danihelka J.</b>, Ermakov N., German D.A., <b>Hájek M.</b>, <b>Hájek O.</b>, <b>Hájková P.</b>, <b>Horsáková V.</b>, <b>Kočí M.</b>, <b>Kubešová S.</b>, <b>Lustyk P.</b>, <b>Nekola J.C.</b>, <b>Preislerová Z.</b>, Resl P. & Valachovič M. (2017) Refugial ecosystems in central Asia as indicators of biodiversity change during the Pleistocene–Holocene transition. <i>Ecological Indicators</i> 77: 357–367. – <a href="http://dx.doi.org/10.1016/j.ecolind.2016.12.033">http://dx.doi.org/10.1016/j.ecolind.2016.12.033</a>
		</li>
        <li>
          <b>Chytrý M.</b>, Pešout P. &amp; Anenchonov O.A. (1993): Syntaxonomy of vegetation of Svjatoj
          Nos Peninsula, Lake Baikal. 1. Non forest communities. <i>Folia Geobotanica et Phytotaxonomica</i> 28: 337–383. – 
          <a href="http://www.sci.muni.cz/botany/chytry/Chytry_etal1993_Folia-Geobot.pdf">
            {" "}
            fulltex in pdf
          </a>
        </li>
        <li>
          <b>Danihelka J.</b> &amp; Soják J. (2012): <i>{" "}Potentilla x nebulosa</i> (Rosaceae), a new intersectional
          nothospecies from the Altai Mountains. <i>Turczaninowia</i> 5: 5–8. –
          <a href="http://is.muni.cz/repo/1090827/turcz_15_4_5-8.pdf">{" "}fulltext in pdf</a>{" "}
        </li>
        <li>
          Ermakov N., <b>Chytrý M.</b> &amp; Valachovič M. (2006): Vegetation of the rock outcrops and
          screes in the forest-steppe and steppe belts of the Altai and Western Sayan Mts., southern
          Siberia. <i>Phytocoenologia</i> 36: 509–545. –
          <a href="http://www.ingentaconnect.com/content/schweiz/phyt/2006/00000036/00000004/art00002">
            {" "}
            fulltext on IngentaConnect
          </a>
        </li>
        <li>
		Hais M., <b>Chytrý M.</b> & <b>Horsák M.</b> (2016) Exposure-related forest-steppe: A diverse landscape type determined by topography and climate. <i>Journal of Arid Environments</i> 135: 75–84. – <i>Phytocoenologia</i> 36: 509–545. –
          <a href="https://www.sciencedirect.com/science/article/abs/pii/S0140196316301537">
            {" "}
            fulltext on ScienceDirect
          </a>
		</li>
        <li>
          Hais M., Komprdová K., Ermakov N. &amp; <b>Chytrý M.</b> (2015): Modelling the Last Glacial
          Maximum environments for a refugium of Pleistocene biota in the Russian Altai Mountains,
          Siberia. <i>Palaeogeography, Palaeoclimatology, Palaeoecology</i> 438: 135–145. –
          <a href="http://www.sciencedirect.com/science/article/pii/S0031018215004137">
            {" "}
            fulltext on ScienceDirect
          </a>
        </li>
        <li>
          <b>Horsák M.</b> &amp; <b>Chytrý M.</b> (2014): Unimodal latitudinal pattern of land-snail species
          richness across northern Eurasian lowlands. <i>PLoS ONE</i> 9: e104035. –
          <a href="http://www.sci.muni.cz/botany/chytry/Horsak-Chytry2014_PLOS-ONE_with-appendix.pdf">
            {" "}
            fulltext in pdf
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b> &amp; <b>Axmanová I.</b> (2013): Exceptionally poor land snail fauna of
          central Yakutia (NE Russia): climatic and habitat determinants of species richness. <i>Polar
          Biology</i> 36: 185–191. –
          <a href="http://link.springer.com/article/10.1007%2Fs00300-012-1249-5">
            {" "}
            fulltext on SpringerLink
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b>, <b>Danihelka J.</b>, Kočí M., <b>Kubešová S.</b>, <b>Lososová Z.</b>, <b>Otýpková Z.</b> &amp;
          <b> Tichý L.</b> (2010): Snail faunas in the Southern Ural forests and their relations to
          vegetation: an analogue of the Early Holocene assemblages of Central Europe? <i>Journal of
          Molluscan Studies</i> 76: 1–10. –
          <a href="https://academic.oup.com/mollus/article/76/1/1/1084149">
            {" "}
            fulltext on Oxford Journals
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b>, <b>Hájková P.</b>, <b>Hájek M.</b>, <b>Danihelka J.</b>, <b>Horsáková V.</b>, Ermakov N., German
          D.A., Kočí M., Lustyk P., Nekola J.C., <b>Preislerová Z.</b> &amp; Valachovič M. (2015): European
          glacial relict snails and plants: environmental context of their modern refugial
          occurrence in southern Siberia. <i>Boreas</i> 44: 638–657. –
          <a href="http://onlinelibrary.wiley.com/doi/10.1111/bor.12133/abstract">
            {" "}
            fulltext on Wiley Online Library
          </a>
        </li>
        <li>
          <b>Horsák M.</b>, <b>Chytrý M.</b>, Pokryszko B.M., <b>Danihelka J.</b>, Ermakov N., <b>Hájek M.</b>, <b>Hájková P.</b>, 
          <b> Kintrová K.</b>, Kočí M., <b>Kubešová S.</b>, Lustyk P., <b>Otýpková Z.</b>, <b>Pelánková B.</b> &amp; Valachovič
          M. (2010): Habitats of relict terrestrial snails in southern Siberia: lessons for the
          reconstruction of palaeoenvironments of full-glacial Europe. <i>Journal of Biogeography</i> 37:
          1450–1462. –
          <a href="https://onlinelibrary.wiley.com/doi/full/10.1111/j.1365-2699.2010.02280.x">
            {" "}
            fulltext in Wiley Online Library
          </a>
        </li>
        <li>
		<b>Horsák M.</b> & Meng S. (2018): <i>Punctum lozeki</i> n. sp. – a new minute land-snail species (Gastropoda: Punctidae) from Siberia and Alaska. <i>Malacologia</i> 62: 11–20. 
		 </li>
        <li>
		<b>Horsák M.</b>, Juřičková L., <b>Horsáková V.</b>, Pokorná A., Pokorný P., Šizling A. & <b>Chytrý M.</b> (2018) Forest snail diversity and its environmental predictors along a sharp climatic gradient in southern Siberia. <i>Acta Oecologica</i> 88: 1–8. – <a href="https://doi.org/10.1016/j.actao.2018.02.009">
            {" "}
            https://doi.org/10.1016/j.actao.2018.02.009
          </a>
		</li>
        <li>
          <b>Horsák M.</b> &amp; Pokryszko B.M. (2010): <i>Vertigo botanicorum</i> sp. nov. (Gastropoda:
          Pulmonata: Vertiginidae)-a new whorl-snail from the Russian Altai Mountains. <i>Zootaxa</i> 2634:
          57–62. –
          <a href="http://www.mapress.com/zootaxa/2010/f/z02634p062f.pdf">fulltext in pdf</a>{" "}
        </li>
        <li>
          Kuneš P., <b>Pelánková B.</b>, <b>Chytrý M.</b>, Jankovská V., Pokorný P. &amp; Petr L. (2008):
          Interpretation of the last-glacial vegetation of eastern-central Europe using modern
          analogues from southern Siberia. <i>Journal of Biogeography</i> 35: 2223–2236. –
          <a href="https://onlinelibrary.wiley.com/doi/10.1111/j.1365-2699.2008.01974.x">
            {" "}
            fulltext on Wiley InterScience
          </a>
        </li>
        <li>
		Logan S.A., <b>Chytrý M.</b> & Wolff K. (2018): Genetic diversity and demographic history of the Siberian lime (Tilia sibirica). Perspectives in Plant Ecology, Evolution and Systematics 33: 9–17. – <a href="https://doi.org/10.1016/j.ppees.2018.04.005">
            {" "}https://doi.org/10.1016/j.ppees.2018.04.005</a>
		 </li>
        <li>
          Magyari E.K., Kuneš P., Jakab G., Sümegi P., <b>Pelánková B.</b>, Schäbitz F., Braun M. &amp;
          <b> Chytrý M.</b> (2014): Late Pleniglacial vegetation in eastern-central Europe: are there modern
          analogues in Siberia? <i>Quaternary Science Reviews</i> 95: 60–79. –
          <a href="http://www.sciencedirect.com/science/article/pii/S0277379114001449">
            {" "}
            fulltext in ScienceDirect
          </a>
        </li>
        <li>
		<b>Nekola J.C.</b>, Chiba S., Coles B.F., Drost C.A., von Proschwitz T. & <b>Horsák M.</b> (2018): A phylogenetic overview of the genus Vertigo O. F. Müller 1773 (Gastropoda: Pulmonata: Pupillidae: Vertigininae). <i>Malacologia</i> 62: 21–161.
		</li>
        <li>
          Nekola J.C., Coles B.F. &amp; <b>Horsák M.</b> (2015): Species assignment in Pupilla (Gastropoda:
          Pulmonata: Pupillidae): integration of DNA-sequence data and conchology. <i>Journal of
          Molluscan Studies</i> 81: 196–216. –
          <a href="http://mollus.oxfordjournals.org/content/early/2014/12/20/mollus.eyu083">
            {" "}
            fulltext on Oxford Journals
          </a>
        </li>
        <li>
          Novák J., Trotsiuk V., Sýkora O., Svoboda M. &amp; <b>Chytrý M.</b> (2014): Ecology of <i>Tilia
          sibirica</i> in a continental hemiboreal forest, southern Siberia: An analogue of a glacial
          refugium of broad-leaved temperate trees? <i>The Holocene</i> 24: 908–918. –
          <a href="http://hol.sagepub.com/content/24/8/908"> fulltext in SAGE Journals</a>
        </li>
        <li>
		<b>Palpurina S.</b>, <b>Chytrý M.</b>, Hölzel N., <b>Tichý L.</b>, Wagner V., <b>Horsák M.</b>, <b>Axmanová I.</b>, <b>Hájek M.</b>, <b>Hájková P.</b>, Freitag M., <b>Lososová Z.</b>, Mathar W., Tzonev R., <b>Danihelka J.</b> & <b>Dřevojan P.</b> (2019) The type of nutrient limitation affects the plant species richness–productivity relationship: Evidence from dry grasslands across Eurasia. <i>Journal of Ecology</i> 107: 1038–1050. – <a href="https://doi.org/10.1111/1365-2745.13084">https://doi.org/10.1111/1365-2745.13084</a>
		</li>
		<li>
		<b>Palpurina S.</b>, Wagner V., von Wehrden H., <b>Hájek M.</b>, <b>Horsák M.</b>, Brinkert A., Hölzel N., Wesche K., Kamp J., <b>Hájková P.</b>, <b>Danihelka J.</b>, Lustyk P., <b>Merunková K.</b>, <b>Preislerová Z.</b>, <b>Kočí M.</b>, <b>Kubešová S.</b>, Cherosov M., Ermakov N., German D., Gogoleva P., Lashchinsky N., Martynenko V. & <b>Chytrý M.</b> (2017) The relationship between plant species richness and soil pH vanishes with increasing aridity across Eurasian dry grasslands. <i>Global Ecology and Biogeography</i> 26: 425–434. DOI: 10.1111/geb.12549. – <a href="https://onlinelibrary.wiley.com/doi/abs/10.1111/geb.12549">fulltext in Wiley Online Library</a>
		</li>
		<li>
		Pavelková Řičánková V., <b>Horsák M.</b>, Hais M., Robovský J. & <b>Chytrý M.</b> (2018): Environmental correlates of the Late Quaternary regional extinctions of large and small Palaearctic mammals. <i>Ecography</i> 41: 516–527. – <a href="http://dx.doi.org/10.1111/ecog.02851">http://dx.doi.org/10.1111/ecog.02851</a>
		</li>
		<li>
          Pavelková Řičánková V., Riegert J., Semančíková E., Hais M., Čejková A. &amp; Prach K.
          (2014): Habitat preferences in gray marmots (<i>Marmota baibacina</i>). <i>Acta Theriologica</i> 59:
          317–324. – <a href="https://www.academia.edu/14618209/Habitat_preferences_in_gray_marmots_Marmota_baibacina_">fulltext in pdf</a>
        </li>
        <li>
          Pavelková Řičánková V., Robovský J. &amp; Riegert J. (2014): Ecological Structure of
          Recent and Last Glacial Mammalian Faunas in Northern Eurasia: The Case of Altai-Sayan
          Refugium. <i>PLoS ONE</i> 9: e85056. –
          <a href="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0085056">
            {" "}
            fulltext in pdf
          </a>
        </li>
        <li>
          Pavelková Řičánková V., Robovský J., Riegert J. &amp; Zrzavý J. (2015): Regional patterns
          of postglacial changes in the Palearctic mammalian diversity indicate retreat to Siberian
          steppes rather than extinction. <i>Scientific Reports</i> 5: 12682. –
          <a href="http://botzool.sci.muni.cz/pubs/Pavelkova-Ricankova_etal2015_Sci-Rep.pdf">
            {" "}
            fulltext in pdf
          </a>
        </li>
        <li>
          <b>Pelánková B.</b> &amp; <b>Chytrý M.</b> (2009): Surface pollen-vegetation relationships in the
          forest- steppe, taiga and tundra landscapes of the Russian Altai Mountains. <i>Review of
          Palaeobotany and Palynology</i> 157: 253–265. –
          <a href="https://www.sciencedirect.com/science/article/pii/S0034666709000906">
            {" "}
            fulltext on ScienceDirect
          </a>
        </li>
        <li>
          <b>Pelánková B.</b>, Kuneš P., <b>Chytrý M.</b>, Jankovská V., Ermakov N. &amp; Svobodová-Svitavská H.
          (2008): The relationships of modern pollen spectra to vegetation and climate along a
          steppe-forest-tundra transition in southern Siberia, explored by decision trees. <i>The
          Holocene</i> 18: 1259–1271. –
          <a href="http://hol.sagepub.com/cgi/content/abstract/18/8/1259">
            {" "}
            fulltext on Sage Journals Online
          </a>
        </li>
        <li>
          Soják J., <b>Danihelka J.</b> &amp; <b>Hájek M.</b> (2011): Potentilla turkestanica, a rare species new
          to the flora of Russia. <i>Turczaninowia</i> 14: 101–105. –
          <a href="http://www.sci.muni.cz/botany/vz/pdf/Sojak_Danihelka_Hajek_2011_Potentilla_turkestanica_turcz_14_2_101-105.pdf">
            {" "}
            fulltext in pdf
          </a>
        </li>
		<li>
		Wagner V., <b>Chytrý M.</b>, <b>Zelený D.</b>, von Wehrden H., Brinkert A., <b>Danihelka J.</b>, Hölzel N., Jansen F., Kamp J., Lustyk P., <b>Merunková K.</b>, <b>Palpurina S.</b>, <b>Preislerová Z.</b> & Wesche K. (2017) Regional differences in soil pH niche among dry grassland plants in Eurasia. <i>Oikos</i> 126: 660–670. – <a href="http://dx.doi.org/10.1111/oik.03369">
            {" "}
            http://dx.doi.org/10.1111/oik.03369
          </a>
		</li>

        <div>
          <h3>Popularizační články</h3>
        </div>
        <li>
          <b>Kubešová S.</b> (2015): Kazachstánem od severu na jih. <i>M Revue</i> 2015(2): 62–65. –{" "}
          <a href="http://www.sci.muni.cz/botany/chytry/Kubesova2015_Kazachstan.pdf">PDF</a>{" "}
        </li>
        <li>
          <b>Chytrý M.</b> (2012): Ekosystémy Sibiře: analogie zaniklé přírody střední Evropy. <i>Živa</i> 4:
          186–188. –{" "}<a href="">PDF</a>
        </li>
        <li>
          <b>Chytrý M.</b>, Pavelková Řičánková V. & <b>Horsák M.</b> (2010): Kde dnes znamená včera. Jihosibiřské
          refugium doby ledové. <i>Živa</i> 89: 526–530. –{" "}
          <a href="http://www.sci.muni.cz/botany/chytry/Chytry2012_Ziva_Sibir.pdf">PDF</a>
        </li>
        <li>
          <b>Horsák M.</b> & <b>Chytrý M.</b> (2010): Krajiny zamrzlé v čase I. Jižní Sibiř - současná analogie
          střední Evropy v době ledové. <i>Živa</i> 2009: 118–120. –{" "}
          <a href="http://www.sci.muni.cz/botany/chytry/Horsak-Chytry2010ab_Ziva.pdf">PDF</a>
        </li>
        <li>
          <b>Horsák M.</b> & <b>Chytrý M.</b> (2010): Krajiny zamrzlé v čase II. Jižní Ural - současná analogie
          střední Evropy ve starém a středním holocénu. <i>Živa</i> 2010: 166–168. –{" "}
          <a href="http://www.sci.muni.cz/botany/chytry/Horsak-Chytry2010ab_Ziva.pdf">PDF</a>
        </li>
        <li>
          Kuneš P. (2008): Předneolitická krajina, vegetace a role moderního člověka ve střední
          Evropě. <i>Živa</i> 2008: 146–150. –{" "}
          <a href="http://www.sci.muni.cz/botany/vz/pdf/Kunes2008_Ziva.pdf">PDF</a>
        </li>
        <li>
          Pokorný P. (2004): Velký bratr uděluje lekci. Sibiřské řešení záhady evropských ledových
          dob. <i>Vesmír</i> 83: 276–281. –{" "}
          <a href="https://vesmir.cz/cz/casopis/archiv-casopisu/2004/cislo-5/velky-bratr-udeluje-lekci.html">fulltext</a>
        </li>
        <div>
          <h3>Média</h3>
        </div>
        <li>
          30/01/2014{" "}
          <a href="http://www.rferl.org/content/russia-mongolia-altai-ice-age-species/25247802.html">
            Ice age thrives in Southern Siberia
          </a>
          , Radio Free Europe
        </li>
        <li>
          23/01/2014{" "}
          <a href="http://www.newscientist.com/article/mg22129533.800-iceage-animals-live-on-in-eurasian-mountain-range.html#.UygwyNtIw8E">
            Ice-age animals live on in Eurasian mountain range
          </a>
          , New Scientist
        </li>
        <li>
          21/01/2013{" "}
          <a href="https://plus7dni.pluska.sk/relax/znicilo-mamuty-iba-globalne-oteplovanie?itm_site=plus7&itm_template=gallery&itm_area=relax&itm_modul=spat-na-clanok">
            Zničilo mamuty iba globálne otepľovanie?
          </a>{" "}
          Plus 7 dní
        </li>
        <li>
          27/11/2012{" "}
          <a href="http://www.ceskatelevize.cz/ivysilani/10095690193-pred-pulnoci/312281381940091/">
            Vegetace doby ledové na dosah? 
          </a>{" "} 
            Česká televize – Před půlnocí
        </li>
        <li>
          05/08/2012{" "}
          <a href="http://zpravy.ihned.cz/cesko/c1-56913430-proc-vyhynuli-mamuti-odpoved-jezdi-cesky-vedec-hledat-na-sibir">
            Proč vyhynuli mamuti? Odpověď jezdí český vědec hledat na Sibiř
          </a>
          , Hospodářské noviny
        </li>
        <li>
          05/2007 {" "}
          <a href="http://www.sci.muni.cz/botany/vz/pdf/Velinsky2007_21-stoleti.pdf">Doba ledová nebyla tak hrozivá</a>
		   , 21. století
        </li>
        <li>
          05/01/2007{" "}
          <a href="https://temata.rozhlas.cz/cesko-v-dobe-ledove-7851787">
            Česko v době ledové
          </a>
          , Český rozhlas
        </li>
      </div>
      
    </div>
  )
};
